
import { Component } from '@theme/component';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} prevButton
 * @property {HTMLElement} nextButton
 */

/** @extends {Component<Refs>} */
export class TestimonialsSlider extends Component {
    requiredRefs = ['scroller', 'prevButton', 'nextButton'];

    /** @type {IntersectionObserver | undefined} */
    #observer;

    /** @type {number | undefined} */
    #scrollTimeout;

    /** @type {number | undefined} */
    #autoplayInterval;

    /** @type {boolean} */
    #autoplayEnabled = false;

    /** @type {number} */
    #autoplaySpeed = 6000;

    connectedCallback() {
        super.connectedCallback();
        
        this.#autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
        const speedValue = this.getAttribute('data-autoplay-speed');
        this.#autoplaySpeed = speedValue ? parseInt(speedValue) : 6000;

        this.#setupIntersectionObserver();

        // Bind buttons
        const { prevButton, nextButton, scroller } = this.refs;

        if (prevButton instanceof HTMLElement) {
            prevButton.addEventListener('click', () => {
                this.previous();
                this.#resetAutoplay();
            });
        }

        if (nextButton instanceof HTMLElement) {
            nextButton.addEventListener('click', () => {
                this.next();
                this.#resetAutoplay();
            });
        }

        // Add scroll event to verify active slide on manual scroll
        if (scroller instanceof HTMLElement) {
            scroller.addEventListener('scroll', () => {
                if (window.innerWidth < 750) return; // Skip manual scroll handling for mobile grid
                if (this.#scrollTimeout) clearTimeout(this.#scrollTimeout);
                this.#scrollTimeout = setTimeout(() => this.#handleIntersect(), 50);
            });

            // Pause autoplay on mouse enter, resume on mouse leave
            scroller.addEventListener('mouseenter', () => this.#stopAutoplay());
            scroller.addEventListener('mouseleave', () => this.#startAutoplay());
            
            // For mobile touch
            scroller.addEventListener('touchstart', () => this.#stopAutoplay(), { passive: true });
            scroller.addEventListener('touchend', () => this.#startAutoplay(), { passive: true });
        }

        // Initial check
        setTimeout(() => {
            if (window.innerWidth < 750) return;
            this.#handleIntersect();
        }, 150);
        this.#startAutoplay();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.#observer) {
            this.#observer.disconnect();
        }
        if (this.#scrollTimeout) clearTimeout(this.#scrollTimeout);
        this.#stopAutoplay();
    }

    #startAutoplay() {
        if (!this.#autoplayEnabled || window.innerWidth < 750) return;
        this.#stopAutoplay();
        this.#autoplayInterval = setInterval(() => this.next(), this.#autoplaySpeed);
    }

    #stopAutoplay() {
        if (this.#autoplayInterval) {
            clearInterval(this.#autoplayInterval);
            this.#autoplayInterval = undefined;
        }
    }

    #resetAutoplay() {
        this.#stopAutoplay();
        this.#startAutoplay();
    }

    #setupIntersectionObserver() {
        const { scroller } = this.refs;
        if (!(scroller instanceof HTMLElement)) return;

        const isMobile = window.innerWidth < 750;

        this.#observer = new IntersectionObserver((entries) => {
            if (window.innerWidth < 750) {
                this.#handleMobileIntersect(entries);
            } else {
                this.#handleIntersect();
            }
        }, {
            root: isMobile ? null : scroller,
            threshold: isMobile ? 0.2 : 0.51
        });

        const slides = this.querySelectorAll('.testimonials-stories__item');
        slides.forEach(slide => this.#observer?.observe(slide));
    }

    /**
     * @param {IntersectionObserverEntry[]} entries 
     */
    #handleMobileIntersect(entries) {
        entries.forEach(entry => {
            const slide = entry.target;
            if (!(slide instanceof HTMLElement)) return;

            const video = slide.querySelector('video');
            if (video instanceof HTMLVideoElement) {
                if (entry.isIntersecting) {
                    video.play().catch(() => { });
                    slide.classList.add('is-active');
                } else {
                    video.pause();
                    slide.classList.remove('is-active');
                }
            }
        });
    }

    #handleIntersect() {
        const slides = Array.from(this.querySelectorAll('.testimonials-stories__item'));
        const { scroller } = this.refs;
        if (!(scroller instanceof HTMLElement)) return;

        const scrollerRect = scroller.getBoundingClientRect();
        const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;

        /** @type {HTMLElement | null} */
        let closestSlide = null;
        let minDistance = Infinity;

        for (const slide of slides) {
            if (!(slide instanceof HTMLElement)) continue;
            const rect = slide.getBoundingClientRect();
            const slideCenter = rect.left + rect.width / 2;
            const distance = Math.abs(scrollerCenter - slideCenter);

            if (distance < minDistance) {
                minDistance = distance;
                closestSlide = slide;
            }

            // Reset state for all
            slide.classList.remove('is-active');
            const video = slide.querySelector('video');
            if (video instanceof HTMLVideoElement) {
                video.pause();
            }
        }

        if (closestSlide) {
            closestSlide.classList.add('is-active');
            const video = closestSlide.querySelector('video');
            if (video instanceof HTMLVideoElement) {
                video.play().catch(() => { });
            }
        }
    }

    next() {
        const { scroller } = this.refs;
        if (!(scroller instanceof HTMLElement)) return;

        const slides = scroller.querySelectorAll('.testimonials-stories__item');
        if (slides.length === 0) return;

        const firstSlide = slides[0];
        if (!(firstSlide instanceof HTMLElement)) return;

        const slideWidth = firstSlide.offsetWidth + 24; // width + gap
        const maxScroll = scroller.scrollWidth - scroller.clientWidth;

        // If we are at the end, scroll back to the beginning
        if (scroller.scrollLeft + 10 >= maxScroll) {
            scroller.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
            scroller.scrollBy({ left: slideWidth, behavior: 'smooth' });
        }
    }

    previous() {
        const { scroller } = this.refs;
        if (!(scroller instanceof HTMLElement)) return;

        const slides = scroller.querySelectorAll('.testimonials-stories__item');
        if (slides.length === 0) return;

        const firstSlide = slides[0];
        if (!(firstSlide instanceof HTMLElement)) return;

        const slideWidth = firstSlide.offsetWidth + 24; // width + gap

        // If we are at the beginning, scroll to the end
        if (scroller.scrollLeft <= 10) {
            const maxScroll = scroller.scrollWidth - scroller.clientWidth;
            scroller.scrollTo({ left: maxScroll, behavior: 'smooth' });
        } else {
            scroller.scrollBy({ left: -slideWidth, behavior: 'smooth' });
        }
    }
}

if (!customElements.get('testimonials-slider')) {
    customElements.define('testimonials-slider', TestimonialsSlider);
}
